<template>
  <div>
    <template v-if="toolboxGroup">
      <IconCalculator size="24" style="padding:10px;"/>
      <p>Calculated Value</p>
    </template>

    <template v-else>
      <div class="set" :data-element-id="elementId" :style="{ width: tileWidth }">
        <div class="subset" v-if="showLabel && !labelAfter">
          <ProsemirrorEditor
              class="text"
              defaultColor="#333"
              defaultFontName="Arial"
              :editable="true"
              :viewer="viewer"
              :elementId="elementData.id"
              :value="elementText"
              @update="value => elementData.text = value"
              @focus="proseFocus()"
              :style="getSecondaryStyle()"
          />
          <Idot :element-data="elementData"/>
        </div>

        <div class="data"  :style="getStyle()">
          <div v-if="elementFieldType === 'value'" class="out-text" :data-element-id="elementId">
            <div v-if="!isMeta" class="text" style="padding-right: 9px" @click="showFormula">{{ getPrecisionValue }}{{ suffix }}</div>
            <div v-else class="text">{{ getComputedText }}</div>
          </div>
          <div v-else-if="elementFieldType === 'notes'" class="out-text" :data-element-id="elementId">
            <div class="text" v-html="formattedNotes"></div>
          </div>
          <div v-else-if="elementFieldType === 'source'" class="out-text" :data-element-id="elementId">
            <div class="text">{{ getComputedSource }}</div>
          </div>
          <div v-else-if="elementFieldType === 'message'" class="out-text" :data-element-id="elementId">
            <div class="text">{{ getComputedMessage }}</div>
          </div>
          <div v-else-if="elementFieldType === 'none'" class="out-text" :data-element-id="elementId">

          </div>
          <Idot v-if="!showLabel" :element-data="elementData"/>
        </div>

        <div class="subset" v-if="showLabel && labelAfter">
          <ProsemirrorEditor
              class="text"
              defaultColor="#333"
              defaultFontName="Arial"
              :editable="true"
              :viewer="viewer"
              :elementId="elementData.id"
              :value="elementText"
              @update="value => elementData.text = value"
              @focus="proseFocus()"
              :style="getSecondaryStyle()"
          />
          <Idot :element-data="elementData"/>
        </div>

    </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {toRefs, watch, computed, onMounted, getCurrentInstance, ref} from 'vue'
import {useGivingTreeStore} from '@/store/givingTree'
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";
import {getValuePropositionAPI, ValuePropositionAPI} from "@/services/value-proposition-api";
import ProsemirrorEditor from "@/views/components/element/ProsemirrorEditor.vue";
import Idot from "@/components/Idot.vue";
import {storeToRefs} from "pinia";
import {useSlidesStore} from "@/store";

const givingTreeStore = useGivingTreeStore()

const props = defineProps({
  elementData: {
    type: Object
  },
  viewer: {
    type: Boolean,
    default: false
  }
})

const slidesStore = useSlidesStore()
const {locale, currency, theme} = storeToRefs(slidesStore)

const {elementData} = toRefs(props)
const elementId = computed(() => elementData.value?.id ?? null)
const elementHookId = computed(() => elementData.value?.hookId ?? '')
const elementText = computed(() => elementData.value?.text ?? 'Label')
const positionFlex = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? 'row' : 'column')
const justifyContent = computed(() => elementData.value?.style.justifyContent ?? '')
const showLabel = computed(() => elementData.value?.labelPosition !== 'none')
const labelAfter = computed(() => elementData.value?.labelPosition === 'bottom' || elementData.value?.labelPosition === 'right')
const labelMargins = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? '0 10px' : '10px 0')
const elementFieldType = computed(() => elementData.value?.fieldType ?? 'value')
const ignoreProseMirror = ref(false)
const isMeta = ref(false)
const tileAlign = computed(() => elementData.value?.style.align ?? 'center')
const metaCurrency = computed(() => {
  return getComputedTextByHookId('meta-currency') ?? 'USD'
})
const metaLocale = computed(() => locale.value ?? 'en-US')
const elementStyle = computed(() => elementData.value?.style ?? {})
const tileWidth = computed(() => elementStyle.value?.containerWidth ? `${elementStyle.value?.containerWidth}px` : '')


const {
  getActiveHookId,
  getComputedValue,
  getComputedType,
  getComputedPrecision,
  getComputedNotes,
  getComputedSource,
  getComputedMessage,
  getComputedText,
  getComputedTextByHookId
} = useComputedTreeValue()

const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

const formattedNotes = computed(() => {
  return getComputedNotes.value?.replace(/\n/g, '<br />');
})

onMounted(() => {

  if (!elementHookId.value) return
  if (elementHookId.value) getActiveHookId(elementHookId.value)
  const value = givingTreeStore.getWidgetValue(elementHookId.value)

  isMeta.value = value?.type === 'meta'
})

const showFormula = async () => {
  const api: ValuePropositionAPI | null = getValuePropositionAPI()

  const widget = givingTreeStore.getWidgetValue(elementHookId.value)

  // PC?

    if (widget.type === 'value-driver') {
      api?.fireUIEvent("vdHandler", {action: "show_vd", valueDriverId: widget.id});
    }
    else if (widget.vpHasFormula) {
      api?.fireUIEvent("varHandler", {action: "show_var", variableUid: widget.uid});
    }
}

const getStyle = () => {
  return {
    fontFamily: theme.value.fontName ?? '',
    fontSize: elementData.value?.style?.fontSize,
    fontWeight: elementData.value?.style?.fontWeight,
    fontStyle: elementData.value?.style?.fontStyle,
    textDecoration: elementData.value?.style?.textDecoration,
    color: elementData.value?.style?.color,
    textAlign: elementData.value?.style?.align,
    display: 'flex',
    lineHeight: elementData.value?.style?.lineHeight ? elementData.value?.style?.lineHeight : 'normal',
    justifyContent: elementData.value?.style?.justifyContent !== '' ? elementData.value?.style?.justifyContent : elementData.value?.style?.align,
    alignItems: elementData.value?.style?.valign
  }
}

const getSecondaryStyle = () => {
  return {
    minWidth: '30px',
    maxWidth: '100%',
    width: px(elementData.value.style?.secondaryWidth, ''),
    height: px(elementData.value.style?.secondaryHeight, ''),
  }
}

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  } else {
    return defaultValue
  }
}

const getPrecisionValue = computed(() => {
  const hasComputedValue = getComputedValue.value !== null && getComputedValue.value !== undefined;
  if (hasComputedValue) {
    return currencyFormat();
  }

  return '';
})


const lockStyle = (locked: boolean) => {
  if (elementData && elementData.value) {
    elementData.value.style.locked = locked
  }
}

const proseFocus = () => {
  ignoreProseMirror.value = false
  lockStyle(true)
}

const mode = computed(() => {
  if (getComputedType.value === 'currency') {
    return 'currency'
  }
  return 'decimal'
})

const suffix = computed(() => {
  if (getComputedType.value === 'percent') {
    return '%'
  }
})

watch(elementHookId, (newVal) => {
  if (newVal) {
    getActiveHookId(newVal)

    const value = givingTreeStore.getWidgetValue(newVal)

    isMeta.value = value.type === 'meta'

  }
}, {deep: true})

const currencyFormat = () => {

  const hasCompactElementData = elementData.value?.compact;
  const value = getComputedValue.value ? getComputedValue.value : 0
  const precision = getComputedPrecision.value

  const notation = value >= 10000 && hasCompactElementData ? 'compact' : 'standard';
  const decimalPlaces = notation === 'compact' ? 1 : precision
  return new Intl.NumberFormat(metaLocale.value, {
    notation: notation,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    style: mode.value,
    currency: metaCurrency.value
  }).format(value);
}
</script>

<style scoped lang="scss">

.subset {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
}

.set {
  display: flex;
  flex-direction: v-bind(positionFlex);
  align-items: v-bind(tileAlign);
  justify-content: v-bind(justifyContent);
}

:deep(p) {
  margin: v-bind(labelMargins);
}


</style>
